import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { Drawer } from '@loadsmart/miranda-react'

import { RequestStatus } from 'core/domain/Request'
import { isPast } from 'core/utils/date'

import {
  type AppointmentFields,
  type Carrier,
  type ScheduleFormValues,
} from '../../domain/Shipment'
import { useGetOpenDockCarrierId } from '../hooks/useGetOpenDockCarrierId'
import { useGetOpenDockWarehouseDetails } from '../hooks/useGetOpenDockWarehouseDetails'
import { DrawerLoadingBody, DrawerShell } from './DrawerShell'
import { DrawerStep } from './DrawerStep'
import { OpenDockDrawerProvider, type OpenDockDrawerValue } from './OpenDockDrawerContext'
import { INITIAL_STEP, type Step } from './steps'

export function OpenDockAppointmentDrawer({
  isOpen,
  onClose,
  shipmentUUID,
  shipmentCarrier,
  stopIndex,
  facilityTimezone,
  onConfirm,
  stopDate,
}: {
  isOpen: boolean
  onClose: () => void
  shipmentUUID: string
  shipmentCarrier: Carrier
  stopIndex: number
  facilityTimezone?: string
  onConfirm: () => void
  stopDate: string
}) {
  const [step, setStep] = useState<Step>(INITIAL_STEP)
  const [createdCarrierId, setCreatedCarrierId] = useState<string>()

  const [fields, setFields] = useState<AppointmentFields>({
    staticFields: {
      loadType: null,
      dock: null,
      appointmentDate: isPast(new Date(stopDate)) ? null : `${stopDate}T12:00:00.000Z`, // Using the same time and timezone as selected in the datepicker
      appointmentTime: null,
    },
    dynamicFields: {},
  })

  function setStaticFields(staticFields: Partial<ScheduleFormValues>) {
    setFields(prev => ({
      ...prev,
      staticFields: {
        ...prev.staticFields,
        ...staticFields,
      },
    }))
  }

  const {
    openDockCarrierId,
    fetchOpenDockCarrierId,
    status: carrierStatus,
  } = useGetOpenDockCarrierId(shipmentUUID, false)

  const effectiveCarrierId = openDockCarrierId ?? createdCarrierId

  const {
    warehouseDetails,
    fetchOpenDockWarehouseDetails,
    status: warehouseStatus,
  } = useGetOpenDockWarehouseDetails(shipmentUUID, stopIndex, false)

  useEffect(() => {
    if (carrierStatus === RequestStatus.Initial && isOpen) {
      fetchOpenDockCarrierId(shipmentUUID)
    }
  }, [fetchOpenDockCarrierId, isOpen, shipmentUUID, carrierStatus])

  useEffect(() => {
    if (warehouseStatus === RequestStatus.Initial && isOpen) {
      fetchOpenDockWarehouseDetails()
    }
  }, [fetchOpenDockWarehouseDetails, isOpen, warehouseStatus])

  useEffect(() => {
    if (warehouseStatus === RequestStatus.Error) {
      onClose()
      toast.error('Failed to load warehouse details. Please try again later.')
    }
  }, [onClose, warehouseStatus])

  useEffect(() => {
    if (carrierStatus === RequestStatus.Error) {
      onClose()
      toast.error('Failed to verify your Opendock account. Please try again later.')
    }
  }, [onClose, carrierStatus])

  if (warehouseStatus === RequestStatus.Pending) {
    return (
      <DrawerShell isOpen={isOpen} onClose={onClose}>
        <DrawerLoadingBody />
        <Drawer.Actions>
          <Drawer.ActionTertiary disabled>Enter Confirmation Number</Drawer.ActionTertiary>
          <Drawer.ActionPrimary disabled>Start</Drawer.ActionPrimary>
        </Drawer.Actions>
      </DrawerShell>
    )
  }

  if (warehouseStatus === RequestStatus.Done && warehouseDetails) {
    const drawerValue: OpenDockDrawerValue = {
      shipmentUUID,
      stopIndex,
      facilityTimezone,
      shipmentCarrier,
      warehouseDetails,
      carrierId: effectiveCarrierId,
      fields,
      setStep,
      setStaticFields,
      onConfirm,
      onClose,
    }

    return (
      <OpenDockDrawerProvider value={drawerValue}>
        <DrawerShell isOpen={isOpen} onClose={onClose}>
          <DrawerStep
            step={step}
            carrierStatus={carrierStatus}
            onCarrierCreated={setCreatedCarrierId}
          />
        </DrawerShell>
      </OpenDockDrawerProvider>
    )
  }

  return null
}
